// frontend/src/pages/admin/CVGeneration/DataEntryTab.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Users, GraduationCap, Briefcase, Award, 
  AlertCircle, CheckCircle, XCircle, ChevronDown, ChevronRight,
  Clock
} from 'lucide-react';
import { staffAPI } from '../../../services/api';
import toast from '../../../utils/toast';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import { CountBadge } from '../../../components/UI/CountBadge';
import EducationSection from './EducationSection';
import WorkExperienceSection from './WorkExperienceSection';
import CertificatesSection from './CertificatesSection';

/**
 * Data Entry Tab for CV Generation
 * 
 * Allows quick entry of CV-related data:
 * - Education records
 * - Work experience
 * - Professional certificates
 * 
 * Features:
 * - Full CRUD (Add, Edit, Delete)
 * - Duplicate detection (blocks duplicates)
 * - Data freshness indicators
 * - Audit trail (created_at, updated_at)
 */
const DataEntryTab = () => {
  // State
  const [staffList, setStaffList] = useState([]);
  const [staffListLoading, setStaffListLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedDepartments, setCollapsedDepartments] = useState(new Set());
  
  // Data for selected employee
  const [educationRecords, setEducationRecords] = useState([]);
  const [workExperienceRecords, setWorkExperienceRecords] = useState([]);
  const [certificateRecords, setCertificateRecords] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  // Fetch ALL staff (paginate through all pages)
  useEffect(() => {
    const fetchAllStaff = async () => {
      try {
        setStaffListLoading(true);
        let allStaff = [];
        let page = 1;
        let hasMore = true;
        
        while (hasMore) {
          const response = await staffAPI.getStaffList({
            page_size: 200,
            page: page,
            employment_status: 'active',
          });
          const results = response.data?.results || response.data || [];
          allStaff = [...allStaff, ...results];
          
          hasMore = response.data?.next !== null && results.length > 0;
          page++;
          
          if (page > 20) break;
        }
        
        // Map to consistent format and filter out system accounts
        const mappedStaff = allStaff
          .filter(s => !['A0001', 'S0000'].includes(s.staff_id))
          .map(s => ({
            staff_id: s.staff_id,
            full_name: s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim(),
            designation: s.designation || '',
            department: s.department?.department_name || s.department || 'Unassigned',
          }));
        
        setStaffList(mappedStaff);
      } catch (error) {
        console.error('Error fetching staff list:', error);
        toast.error('Failed to load staff list');
      } finally {
        setStaffListLoading(false);
      }
    };
    fetchAllStaff();
  }, []);

  // Fetch data for selected employee
  const fetchEmployeeData = useCallback(async (staffId) => {
    if (!staffId) return;
    
    setDataLoading(true);
    try {
      const [eduRes, workRes, certRes] = await Promise.all([
        staffAPI.getEducation({ education_staff_id: staffId }),
        staffAPI.getWorkExperience({ work_exp_staff_id: staffId }),
        staffAPI.getCertificates({ cert_staff_id: staffId }),
      ]);
      
      setEducationRecords(eduRes.data?.results || eduRes.data || []);
      setWorkExperienceRecords(workRes.data?.results || workRes.data || []);
      setCertificateRecords(certRes.data?.results || certRes.data || []);
    } catch (error) {
      console.error('Error fetching employee data:', error);
      toast.error('Failed to load employee data');
    } finally {
      setDataLoading(false);
    }
  }, []);

  // Fetch data when employee is selected
  useEffect(() => {
    if (selectedEmployee) {
      // Clear old data immediately when switching employees to prevent stale data display
      setEducationRecords([]);
      setWorkExperienceRecords([]);
      setCertificateRecords([]);
      fetchEmployeeData(selectedEmployee.staff_id);
    } else {
      setEducationRecords([]);
      setWorkExperienceRecords([]);
      setCertificateRecords([]);
    }
  }, [selectedEmployee, fetchEmployeeData]);

  // Filter employees by search term
  const filteredEmployees = useMemo(() => {
    if (!searchTerm.trim()) return staffList;
    const search = searchTerm.toLowerCase();
    return staffList.filter(emp =>
      emp.full_name?.toLowerCase().includes(search) ||
      emp.staff_id?.toLowerCase().includes(search) ||
      emp.designation?.toLowerCase().includes(search) ||
      emp.department?.toLowerCase().includes(search)
    );
  }, [staffList, searchTerm]);

  // Group employees by department
  const groupedByDepartment = useMemo(() => {
    const groups = {};
    filteredEmployees.forEach(emp => {
      const dept = emp.department || 'Unassigned';
      if (!groups[dept]) {
        groups[dept] = [];
      }
      groups[dept].push(emp);
    });
    
    const sortedDepts = Object.keys(groups).sort((a, b) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });
    
    return { groups, sortedDepts };
  }, [filteredEmployees]);

  const toggleDepartmentExpand = (department) => {
    setCollapsedDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(department)) {
        newSet.delete(department);
      } else {
        newSet.add(department);
      }
      return newSet;
    });
  };

  // Calculate data completeness for an employee
  const getDataCompleteness = (emp) => {
    // This is a simplified check - in production, you'd want to check actual records
    // For now, we'll show a placeholder
    return { score: 0, hasEducation: false, hasWorkExp: false, hasCerts: false };
  };

  // Refresh data after CRUD operations
  const handleDataChange = useCallback(() => {
    if (selectedEmployee) {
      fetchEmployeeData(selectedEmployee.staff_id);
    }
  }, [selectedEmployee, fetchEmployeeData]);

  return (
    <div className="space-y-4">
      {/* Split Panel Layout */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="grid grid-cols-12 divide-x divide-gray-200" style={{ height: 'calc(100dvh - 340px)', minHeight: '500px' }}>
          
          {/* Left Panel - Employee List (3/12) — wide enough for name scan */}
          <div className="col-span-12 md:col-span-3 flex flex-col h-full overflow-hidden">
            {/* Search Header — 24px gutter from panel edge (DS 8dp: px-6) */}
            <div className="px-6 py-3 border-b border-gray-200 space-y-3 flex-shrink-0">
              <DebouncedSearchInput
                value={searchTerm}
                onSearch={setSearchTerm}
                placeholder="Search employees..."
              />
              <div className="flex items-center justify-between gap-3 text-sm text-gray-600">
                <span>Select an employee</span>
                <CountBadge count={filteredEmployees.length} theme="admin" size="md" max={9999} />
              </div>
            </div>

            {/* Employee List */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {staffListLoading ? (
                <div className="flex items-center justify-center h-32">
                  <LoadingSpinner size="md" />
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-500 px-6">
                  <Users className="w-8 h-8 mb-2 text-gray-300" />
                  <p className="text-sm text-center">
                    {searchTerm ? 'No employees match your search' : 'No employees available'}
                  </p>
                </div>
              ) : (
                <div>
                  {groupedByDepartment.sortedDepts.map((department) => {
                    const deptEmployees = groupedByDepartment.groups[department];
                    const isExpanded = !collapsedDepartments.has(department);

                    return (
                      <div key={department} className="border-b border-gray-100 last:border-b-0">
                        {/* Department Header */}
                        <div 
                          className="flex items-center gap-2.5 px-6 py-2.5 bg-gray-100 border-b border-gray-200 border-l-2 border-l-transparent cursor-pointer hover:bg-gray-200 sticky top-0 z-10 transition-colors"
                          onClick={() => toggleDepartmentExpand(department)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          )}
                          <span className="flex-1 font-medium text-sm text-gray-700 truncate">
                            {department}
                          </span>
                          <CountBadge count={deptEmployees.length} theme="admin" size="md" max={9999} className="flex-shrink-0" />
                        </div>

                        {/* Employee List for this Department */}
                        {isExpanded && (
                          <ul className="divide-y divide-gray-100">
                            {deptEmployees.map((employee) => {
                              const isSelected = selectedEmployee?.staff_id === employee.staff_id;
                              const designation = employee.designation || 'No designation';

                              return (
                                <li
                                  key={employee.staff_id}
                                  onClick={() => setSelectedEmployee(employee)}
                                  className={`
                                    px-6 py-2.5 cursor-pointer transition-colors border-l-2
                                    ${isSelected ? 'bg-red-50 border-l-red-600' : 'table-row border-l-transparent'}
                                  `}
                                >
                                  <div className="flex items-start gap-2.5">
                                    {/* Spacer = chevron column — keeps name inset & aligned under dept label */}
                                    <span className="w-4 flex-shrink-0" aria-hidden="true" />
                                    <div className="min-w-0 flex-1">
                                      <span className="font-medium text-gray-900 text-sm truncate block">
                                        {employee.full_name}
                                      </span>
                                      <p className="text-xs text-gray-500 truncate mt-0.5">
                                        {designation} · {employee.staff_id}
                                      </p>
                                    </div>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Data Entry Forms (9/12) */}
          <div className="col-span-12 md:col-span-9 flex flex-col h-full overflow-hidden">
            {!selectedEmployee ? (
              // No employee selected
              <div className="flex-1 flex flex-col items-center justify-center text-gray-500 p-8">
                <Users className="w-16 h-16 mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">Select an Employee</h3>
                <p className="text-sm text-center max-w-md">
                  Choose an employee from the list to view and edit their CV data including education, work experience, and professional certificates.
                </p>
              </div>
            ) : (
              // Employee selected - show data entry sections
              <div className="flex-1 overflow-y-auto">
                {/* Employee Header */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                    <div className="min-w-0">
                      <h2 className="text-base sm:text-xl font-semibold text-gray-900 truncate">{selectedEmployee.full_name}</h2>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">
                        {selectedEmployee.designation || 'No designation'} • {selectedEmployee.staff_id}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        <GraduationCap className="w-3 h-3" />
                        {educationRecords.length} <span className="hidden sm:inline">Education</span>
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <Briefcase className="w-3 h-3" />
                        {workExperienceRecords.length} <span className="hidden sm:inline">Work Exp</span>
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        <Award className="w-3 h-3" />
                        {certificateRecords.length} <span className="hidden sm:inline">Certs</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Data Sections */}
                {dataLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <LoadingSpinner size="lg" />
                  </div>
                ) : (
                  <div className="p-6 space-y-6">
                    {/* Education Section */}
                    <EducationSection
                      staffId={selectedEmployee.staff_id}
                      records={educationRecords}
                      onDataChange={handleDataChange}
                    />

                    {/* Work Experience Section */}
                    <WorkExperienceSection
                      staffId={selectedEmployee.staff_id}
                      records={workExperienceRecords}
                      onDataChange={handleDataChange}
                    />

                    {/* Certificates Section */}
                    <CertificatesSection
                      staffId={selectedEmployee.staff_id}
                      records={certificateRecords}
                      onDataChange={handleDataChange}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataEntryTab;

